import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactPlayer from 'react-player';
import { ArrowLeft, Share2, Download, Bookmark, Users, Clock, MessageCircleQuestion, ClipboardList, Target, ChevronDown, ChevronRight, Sparkles, Loader2, FileText, FileDown, MessageSquare, Film, Pencil, Check, X, Trash2, Upload, Youtube, ExternalLink } from 'lucide-react';
import { getRecording, getBookmarks, getRecordingNotes, generateNotesOnDemand, getRecordingChat, getSessionGroupRecordings, deleteRecording, getRecordingAssets as fetchRecordingAssets, getProcessingStatus, getAssetDownloadUrl } from '../../../../services/recordingService';
import { getTranscript } from '../../../../services/transcriptService';
import { updateMeetingRecordingName } from '../../../../services/meetingServices';
import TranscriptViewer from './TranscriptViewer';
import ChatViewer from './ChatViewer';
import RecordingShareModal from './RecordingShareModal';
import ClipsPanel from './ClipsPanel';
import ClipPlayerModal from './ClipPlayerModal';
import ClipEditor from './ClipEditor';
import { toast } from 'react-toastify';
import { useWhiteLabel } from '../../../../context/WhiteLabelContext';
import ParticipantsPopup from './ParticipantsPopup';
import VideoIntegrationService from '../../../../services/videoIntegrationService';

const RecordingDetail = () => {
  const { recordingId } = useParams();
  const navigate = useNavigate();
  const playerRef = useRef(null);
  const { branding: wlBranding } = useWhiteLabel();

  const [recording, setRecording] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [bookmarks, setBookmarks] = useState([]);
  const [notes, setNotes] = useState(null);
  const [currentTimeMs, setCurrentTimeMs] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const [expandedSection, setExpandedSection] = useState('summary');
  const [notesGenerating, setNotesGenerating] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  // Which part's assets the Export dropdown currently shows. Used only when
  // sessionRecordings.length > 1. Defaults to the currently-viewed part; user
  // can switch via the <select> at the top of the dropdown without leaving
  // this page.
  const [exportSelectedPartIdx, setExportSelectedPartIdx] = useState(0);
  const [chatMessages, setChatMessages] = useState([]);
  const [rightSidebarTab, setRightSidebarTab] = useState('transcript');
  const [sessionRecordings, setSessionRecordings] = useState([]);
  const [activeRecordingIndex, setActiveRecordingIndex] = useState(0);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);
  const titleInputRef = useRef(null);
  const [showParticipants, setShowParticipants] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Multi-layout recording state
  const [recordingAssets, setRecordingAssets] = useState([]);
  const [activeLayoutType, setActiveLayoutType] = useState(null);
  const [layoutProcessingStatus, setLayoutProcessingStatus] = useState(null);

  // Clip editor state — clip editor opens inline below the player; clipsRefreshKey
  // forces ClipsPanel to re-fetch immediately after a successful save instead of
  // waiting on the next poll tick.
  const [clipEditorOpen, setClipEditorOpen] = useState(false);
  const [clipsRefreshKey, setClipsRefreshKey] = useState(0);
  // Clip currently being played in the in-page modal player. null = closed.
  const [playingClip, setPlayingClip] = useState(null);
  // Anchor used to smooth-scroll the editor into the viewport when the user
  // clicks "+ Create clip" — without it, the editor appears below the player
  // off-screen and the user has to scroll down manually to find it.
  const clipEditorAnchorRef = useRef(null);

  const [showUploadMenu, setShowUploadMenu] = useState(false);
  const [videoIntegration, setVideoIntegration] = useState(null);
  const [videoError, setVideoError] = useState(false);
  const [uploadingPlatform, setUploadingPlatform] = useState(null);


  const downloadFile = (content, filename) => {
    const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const [videoDownloading, setVideoDownloading] = useState(false);
  const [showVideoDownloadMenu, setShowVideoDownloadMenu] = useState(false);
  const handleDownloadVideo = async (recOverride) => {
    const rec = recOverride || activeRecording;
    const recId = rec?.id || rec?._id || recordingId;
    if (!recId || videoDownloading) return;
    const token = localStorage.getItem('token');
    const downloadUrl = `${process.env.REACT_APP_BASE_URL}/recording/${recId}/download`;
    try {
      setVideoDownloading(true);
      const resp = await fetch(downloadUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'rlink-token': token,
        },
      });
      const blob = await resp.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `${rec?.name || recording?.name || 'recording'}.mp4`;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Video download failed:', err);
      // Fallback: use anchor approach with token in query
      const fallbackUrl = `${downloadUrl}?token=${encodeURIComponent(token)}`;
      const a = document.createElement('a');
      a.href = fallbackUrl;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } finally {
      setVideoDownloading(false);
    }
  };

  const buildNotesText = () => {
    const n = notes;
    const r = recording;
    const lines = [`Meeting Notes: ${r.name || 'Untitled'}`, `Date: ${new Date(r.createdAt).toLocaleString()}`, ''];
    if (n?.summary?.purpose || r.summary) {
      lines.push('## Summary', n?.summary?.purpose || r.summary, '');
    }
    if (n?.summary?.keyTakeaways?.length || r.keyPoints?.length) {
      lines.push('## Key Takeaways');
      (n?.summary?.keyTakeaways || r.keyPoints).forEach(p => lines.push(`  - ${p}`));
      lines.push('');
    }
    if (n?.summary?.topics?.length || r.topics?.length) {
      lines.push('## Topics Discussed');
      (n?.summary?.topics || r.topics).forEach(t => lines.push(`  - ${t}`));
      lines.push('');
    }
    if (n?.summary?.nextSteps?.length) {
      lines.push('## Next Steps');
      n.summary.nextSteps.forEach(s => lines.push(`  - ${s}`));
      lines.push('');
    }
    if (n?.yourQuestions?.length) {
      lines.push('## Your Questions');
      n.yourQuestions.forEach(q => {
        lines.push(`  Q: ${q.question}`);
        if (q.answer) lines.push(`  A: ${q.answer}`);
        lines.push('');
      });
    }
    if (n?.theirQuestions?.length) {
      lines.push('## Their Questions');
      n.theirQuestions.forEach(q => {
        lines.push(`  Q: ${q.question} (asked by ${q.askedBy})`);
        if (q.answer) lines.push(`  A: ${q.answer}`);
        lines.push('');
      });
    }
    if (n?.assignments?.length || r.actionItems?.length) {
      lines.push('## Action Items');
      if (n?.assignments?.length) {
        n.assignments.forEach(a => lines.push(`  [ ] ${a.task} (assigned to: ${a.assignee})`));
      } else {
        r.actionItems?.forEach(a => lines.push(`  [ ] ${a}`));
      }
      lines.push('');
    }
    return lines.join('\n');
  };

  const buildTranscriptText = () => {
    if (!transcript.length) return '';
    const formatTs = (ms) => {
      const totalSec = Math.floor(ms / 1000);
      const m = Math.floor(totalSec / 60);
      const s = totalSec % 60;
      return `${m}:${s.toString().padStart(2, '0')}`;
    };
    return transcript.map(seg => {
      const speaker = seg.speakerName || seg.speaker || 'Unknown';
      const ts = seg.startMs ?? seg.timestamp ?? 0;
      return `[${formatTs(ts)}] ${speaker}: ${seg.text}`;
    }).join('\n');
  };

  const handleDownloadNotes = () => {
    const text = buildNotesText();
    const safeName = (recording.name || 'meeting').replace(/[^a-zA-Z0-9_-]/g, '_');
    downloadFile(text, `${safeName}_notes.txt`);
    setShowDownloadMenu(false);
  };

  const handleDownloadTranscript = () => {
    const text = buildTranscriptText();
    const safeName = (recording.name || 'meeting').replace(/[^a-zA-Z0-9_-]/g, '_');
    downloadFile(text, `${safeName}_transcript.txt`);
    setShowDownloadMenu(false);
  };

  // Multi-recording: derive active recording and filtered transcript
  const activeRecording = sessionRecordings.length > 1
    ? sessionRecordings[activeRecordingIndex]
    : recording;

  // The URL param `recordingId` always points at the part the user originally
  // navigated to, not the part they're currently viewing. Asset downloads are
  // scoped by {recordingId, assetId} on the backend, so we must use the ID of
  // the active part or Part 2+ asset downloads 404.
  const activeRecordingId = activeRecording?.id || activeRecording?._id || recordingId;

  // Multi-layout: determine the video URL based on selected layout
  // Use activeRecording (the currently-selected part), not the initial recording
  // Multi-layout-web fan-out stamps rawRecordingMode='web' (schema enum doesn't
  // accept 'multi-layout-web'), so we also check recorderConfig.mode to surface
  // the layout switcher for those 4-asset recordings.
  const isMultiLayout = (
    activeRecording?.rawRecordingMode === 'individual'
    || activeRecording?.rawRecordingMode === 'selfhosted'
    || activeRecording?.recorderConfig?.mode === 'multi-layout-web'
  ) && recordingAssets.length > 0;
  const activeVideoUrl = useMemo(() => {
    if (activeLayoutType && recordingAssets.length > 0) {
      const asset = recordingAssets.find(
        a => a.assetType === activeLayoutType && a.processingStatus === 'completed'
      );
      if (asset?.fileUrl) return asset.fileUrl;
    }
    return activeRecording?.s3VideoUrl || activeRecording?.fileUrl;
  }, [activeLayoutType, recordingAssets, activeRecording]);

  // Collapse the raw status fields (top-level `status`, worker-pipeline
  // `layoutProcessingStatus`, S3 `compressionStatus`) into a single user-
  // facing pipeline snapshot so the header badge, the banner, and the
  // video-area fallback all agree on what to show. Users were seeing
  // "Meeting Notes" when the recording was still being finalized and
  // assumed it was lost.
  //
  // Returns { state, phaseTitle, phaseDetail }:
  //   state        — 'recording' | 'processing' | 'failed' | 'ready' | 'unknown'
  //   phaseTitle   — short label for the banner/card headline
  //   phaseDetail  — longer body text for the banner/card
  const pipelineInfo = useMemo(() => {
    const r = activeRecording;
    if (!r) return { state: 'unknown', phaseTitle: '', phaseDetail: '' };
    if (r.status === 'failed' || r.layoutProcessingStatus === 'failed') {
      return {
        state: 'failed',
        phaseTitle: 'Recording failed',
        phaseDetail: 'This recording could not be processed. If this keeps happening, please contact support.',
      };
    }

    const hasVideo = !!(r.fileUrl || r.s3VideoUrl) ||
      recordingAssets.some(a => a.processingStatus === 'completed' && a.fileUrl);

    const isLive = r.status === 'recording'
      || r.layoutProcessingStatus === 'record-pending'
      || r.layoutProcessingStatus === 'recording'
      || r.layoutProcessingStatus === 'record-paused';
    if (isLive) {
      // Edge case: parent is stuck in a 'record-*' state but the worker has
      // already uploaded all assets. Treat as ready — the user can play the
      // video right now and the stale parent flag is just bookkeeping.
      if (hasVideo && r.status !== 'recording') {
        return { state: 'ready', phaseTitle: '', phaseDetail: '' };
      }
      return {
        state: 'recording',
        phaseTitle: 'Recording in progress',
        phaseDetail: 'This meeting is still live. The video, transcript, and AI notes will appear here when the session ends.',
      };
    }

    // Meeting has ended — figure out which finalization step we're in so the
    // message accurately reflects what the worker is doing. Order matters:
    // check the upload/compression stage first because it's the last step
    // before the video becomes playable.
    const lps = r.layoutProcessingStatus;
    const cs = r.compressionStatus;

    // If we already have at least one playable asset, the recording is
    // viewable regardless of any stale "still finalizing" flags on the
    // parent doc. We've seen workers upload all assets but forget to flip
    // the parent to 'completed' — that left users staring at a "Wrapping
    // up" banner forever.
    if (hasVideo) {
      return { state: 'ready', phaseTitle: '', phaseDetail: '' };
    }

    if (cs === 'uploading') {
      return {
        state: 'processing',
        phaseTitle: 'Uploading your recording',
        phaseDetail: 'Meeting ended. The final video is being uploaded to secure storage — this page will refresh automatically when it\'s ready.',
      };
    }
    if (cs === 'downloading' || cs === 'compressing' || lps === 'processing') {
      return {
        state: 'processing',
        phaseTitle: 'Cropping and encoding your recording',
        phaseDetail: 'Meeting ended. We\'re trimming the video to the recorded timeframe and re-encoding it for playback. This can take several minutes for longer sessions.',
      };
    }
    if (lps === 'raw') {
      return {
        state: 'processing',
        phaseTitle: 'Preparing your recording',
        phaseDetail: 'Meeting ended. The raw recording has been captured and is queued for cropping and encoding.',
      };
    }
    if (lps === 'record-stopping') {
      return {
        state: 'processing',
        phaseTitle: 'Wrapping up recording',
        phaseDetail: 'Meeting ended. We\'re finishing the capture before starting post-processing.',
      };
    }
    if (!hasVideo && r.status === 'completed' && r.recordingType !== 'chat' && r.recordingType !== 'transcript_only') {
      // Marked completed but no playable asset yet — worker is still assembling
      return {
        state: 'processing',
        phaseTitle: 'Processing your recording',
        phaseDetail: 'Meeting ended. Your recording was captured and is being prepared. This page will refresh automatically when it\'s ready.',
      };
    }

    return { state: 'ready', phaseTitle: '', phaseDetail: '' };
  }, [activeRecording, recordingAssets]);

  const pipelineState = pipelineInfo.state;

  // Filter chat messages to the active recording's time window
  // If recording has its own chatS3Key, messages are already recording-specific — show all.
  // If using fallback chat (no chatS3Key), filter by time window or return empty if unknown.
  const windowChatMessages = React.useMemo(() => {
    if (!chatMessages.length) return chatMessages;
    const rec = activeRecording || recording;
    if (!rec) return chatMessages;

    // Recording-specific or session-specific chat — messages are already filtered by backend
    if (rec.chatS3Key || rec.originalChatUrl || rec.recordingType === 'chat') return chatMessages;

    // Fallback chat: must filter by recording time window to avoid stale messages
    const recStartMs = rec.recordingStartedAt
      ? new Date(rec.recordingStartedAt).getTime()
      : (rec.createdAt && rec.duration)
        ? new Date(rec.createdAt).getTime() - rec.duration * 1000
        : null;
    const recEndMs = rec.createdAt ? new Date(rec.createdAt).getTime() : null;

    // Can't determine time window — don't show stale fallback messages
    if (!recStartMs) return [];

    const grace = 10000;
    const filtered = chatMessages.filter(msg => {
      const ts = msg.timestamp || 0;
      return ts >= (recStartMs - grace) && (!recEndMs || ts <= recEndMs + grace);
    });
    return filtered;
  }, [chatMessages, recording, activeRecording]);

  const activeTranscript = useMemo(() => {
    if (sessionRecordings.length <= 1) return transcript;
    const rec = sessionRecordings[activeRecordingIndex];
    if (!rec) return transcript;
    const recStartMs = rec.recordingStartedAt
      ? new Date(rec.recordingStartedAt).getTime()
      : (rec.createdAt ? new Date(rec.createdAt).getTime() - (rec.duration || 0) * 1000 : null);
    const recEndMs = rec.createdAt
      ? new Date(rec.createdAt).getTime()
      : (recStartMs ? recStartMs + (rec.duration || 0) * 1000 : null);
    if (!recStartMs) return transcript;
    const grace = 10000;
    return transcript.filter(seg => {
      const ts = seg.startMs ?? seg.timestamp ?? 0;
      return ts >= (recStartMs - grace) && (!recEndMs || ts <= recEndMs + grace);
    });
  }, [transcript, sessionRecordings, activeRecordingIndex]);

  const buildChatText = () => {
    const windowMsgs = windowChatMessages;
    if (!windowMsgs.length) return '';
    const lines = [
      `${wlBranding.companyName || 'R-Link Studio'} Chat Export`,
      `Recording: ${recording.name || 'Untitled'}`,
      `Date: ${new Date(recording.createdAt).toLocaleString()}`,
      `Total Messages: ${windowMsgs.length}`,
      '='.repeat(50),
      '',
    ];
    windowMsgs.filter(m => !m.isDeleted).forEach(msg => {
      const time = new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      lines.push(`[${time}] ${msg.userName}:`);
      if (msg.replyTo) {
        lines.push(`  -> Replying to ${msg.replyTo.user}: "${msg.replyTo.message || '[Media]'}"`);
      }
      if (msg.image) lines.push(`  [Image attached]`);
      if (msg.file) lines.push(`  [File: ${msg.file.name}]`);
      if (msg.text) lines.push(`  ${msg.text}`);
      if (msg.isPinned) lines.push(`  [Pinned]`);
      if (msg.reactions && Object.keys(msg.reactions).length > 0) {
        const reactionStr = Object.entries(msg.reactions)
          .map(([emoji, users]) => `${emoji}(${Array.isArray(users) ? users.length : 0})`)
          .join(' ');
        lines.push(`  Reactions: ${reactionStr}`);
      }
      lines.push('');
    });
    return lines.join('\n');
  };

  const handleDownloadChat = () => {
    const text = buildChatText();
    const safeName = (recording.name || 'meeting').replace(/[^a-zA-Z0-9_-]/g, '_');
    downloadFile(text, `${safeName}_chat.txt`);
    setShowDownloadMenu(false);
  };

  useEffect(() => {
    loadRecordingData();
  }, [recordingId]);

  // Reload chat + assets + notes when switching recording tabs — each part
  // has its own distinct WebM/MP4 assets, chat snapshot, and AI notes.
  useEffect(() => {
    setVideoError(false);
    if (sessionRecordings.length <= 1) return;
    const rec = sessionRecordings[activeRecordingIndex];
    if (!rec) return;
    // Always refetch when the active part changes. We used to skip when
    // rec.id === recordingId (the URL-param part) on the assumption the
    // initial load had already populated the right assets — but that
    // breaks the "Part 1 → Part 2 → Part 1" flow: by the time we come
    // back to Part 1, recordingAssets in state is whatever Part 2 set,
    // and skipping leaves the user playing Part 2's video on Part 1.

    // Reload chat
    getRecordingChat(rec.id)
      .then(data => setChatMessages(data?.messages || []))
      .catch(() => setChatMessages([]));

    // Reload assets (CRITICAL: each part has different WebMs)
    if (
      rec.rawRecordingMode === 'individual'
      || rec.rawRecordingMode === 'selfhosted'
      || rec.recorderConfig?.mode === 'multi-layout-web'
    ) {
      fetchRecordingAssets(rec.id)
        .then(data => {
          const assets = data?.assets || [];
          setRecordingAssets(assets);
          setLayoutProcessingStatus(rec.layoutProcessingStatus || null);

          // Re-pick the default layout for this part. Pertrack assets are the
          // source of truth — the legacy Xvfb-captured gallery_view/focus/speaker_*
          // outputs truncate to ~25% of the actual capture window for reasons
          // we haven't yet root-caused, so we prefer the pertrack mirrors when
          // they exist.
          const preferred = [
            'gallery_view_pertrack', 'focus_pertrack', 'speaker_top_pertrack', 'speaker_side_pertrack',
            'gallery_view',          'focus',          'speaker_top',          'speaker_side',
          ];
          let pick = null;
          if (rec.primaryAssetType) {
            const pertrackEquivalent = `${rec.primaryAssetType}_pertrack`;
            const pertrackAsset = assets.find(x => x.assetType === pertrackEquivalent && x.processingStatus === 'completed');
            if (pertrackAsset) pick = pertrackEquivalent;
            else {
              const a = assets.find(x => x.assetType === rec.primaryAssetType && x.processingStatus === 'completed');
              if (a) pick = rec.primaryAssetType;
            }
          }
          if (!pick) {
            for (const t of preferred) {
              if (assets.find(x => x.assetType === t && x.processingStatus === 'completed')) { pick = t; break; }
            }
          }
          if (pick) setActiveLayoutType(pick);
        })
        .catch(err => console.error('Failed to reload assets for part:', err));
    } else {
      setRecordingAssets([]);
      setActiveLayoutType(null);
    }
  }, [activeRecordingIndex, sessionRecordings, recordingId]);

  // Generic poll while the recording isn't yet playable. Re-fetches the
  // top-level Recording doc so status / fileUrl / s3VideoUrl / sessionRecordings
  // refresh automatically — users don't need to manually reload the page to
  // find out whether their meeting was captured. The individual-mode loop
  // below handles layout-asset progress in parallel.
  useEffect(() => {
    if (pipelineState !== 'recording' && pipelineState !== 'processing') return;
    let cancelled = false;
    const intervalId = setInterval(async () => {
      try {
        const recData = await getRecording(recordingId);
        if (cancelled) return;
        const rec = recData?.recording;
        if (rec && typeof rec === 'object') {
          setRecording((prev) => ({ ...(prev || {}), ...rec }));
        }
      } catch (err) {
        console.warn('[RecordingDetail] status poll failed:', err?.message || err);
      }
    }, 10000);
    return () => { cancelled = true; clearInterval(intervalId); };
  }, [pipelineState, recordingId]);

  // Poll layout processing status when recording is still processing
  useEffect(() => {
    if (!recording?.rawRecordingMode || recording.rawRecordingMode !== 'individual') return;
    if (layoutProcessingStatus === 'completed' || layoutProcessingStatus === 'failed') return;
    if (!layoutProcessingStatus || layoutProcessingStatus === 'raw' || layoutProcessingStatus === 'processing') {
      const pollInterval = setInterval(async () => {
        try {
          const statusData = await getProcessingStatus(recordingId);
          setLayoutProcessingStatus(statusData.processingStatus);
          setRecordingAssets(statusData.assets || []);
          if (statusData.processingStatus === 'completed' || statusData.processingStatus === 'failed') {
            clearInterval(pollInterval);
            // Auto-select primary layout when processing completes
            if (statusData.processingStatus === 'completed' && !activeLayoutType) {
              // Prefer Full Stage (selfhosted) if available, then primary asset type
              const selfhostedAsset = (statusData.assets || []).find(
                a => a.assetType === 'selfhosted_composited' && a.processingStatus === 'completed'
              );
              if (selfhostedAsset) {
                setActiveLayoutType('selfhosted_composited');
              } else {
                // Prefer pertrack mirror if available — legacy gallery_view (Xvfb)
                // truncates badly; pertrack files are the source of truth.
                const primary = statusData.primaryAssetType || 'gallery_view';
                const pertrackPrimary = `${primary}_pertrack`;
                const pertrackAsset = (statusData.assets || []).find(
                  a => a.assetType === pertrackPrimary && a.processingStatus === 'completed'
                );
                if (pertrackAsset) {
                  setActiveLayoutType(pertrackPrimary);
                } else {
                  const primaryAsset = (statusData.assets || []).find(
                    a => a.assetType === primary && a.processingStatus === 'completed'
                  );
                  if (primaryAsset) setActiveLayoutType(primary);
                }
              }
            }
          }
        } catch (err) {
          console.error('Failed to poll processing status:', err);
        }
      }, 10000);
      return () => clearInterval(pollInterval);
    }
  }, [recording?.rawRecordingMode, layoutProcessingStatus, recordingId]);

  // Auto-generate AI notes if missing/failed and transcript exists
  const hasTriggeredAutoGenRef = useRef(false);
  useEffect(() => {
    if (
      recording &&
      !notes &&
      !recording.summary &&
      transcript.length > 0 &&
      !notesGenerating &&
      !hasTriggeredAutoGenRef.current &&
      recording.notesStatus !== 'generating' &&
      recording.summaryStatus !== 'generating'
    ) {
      hasTriggeredAutoGenRef.current = true;
      console.log('[RecordingDetail] Auto-generating notes (summaryStatus:', recording.summaryStatus, 'notesStatus:', recording.notesStatus, ')');
      triggerNotesGeneration();
    }
  }, [recording, transcript, notes]);

  // Fetch video integration status (YouTube/Vimeo)
  useEffect(() => {
    VideoIntegrationService.getStatus()
      .then(res => { if (res.success && res.enabled) setVideoIntegration(res); })
      .catch(() => {});
  }, []);

  const handleUploadToPlatform = async (platform) => {
    setShowUploadMenu(false);
    setUploadingPlatform(platform);
    try {
      const res = await VideoIntegrationService.triggerUpload(
        activeRecording?.id || activeRecording?._id || recordingId,
        platform
      );
      if (res.success) {
        toast.success(`Upload to ${platform === 'youtube' ? 'YouTube' : 'Vimeo'} queued`);
        // Poll for status
        const pollInterval = setInterval(async () => {
          try {
            const status = await VideoIntegrationService.getUploadStatus(activeRecording?.id || activeRecording?._id || recordingId);
            const upload = status?.videoUpload?.[platform];
            if (upload?.status === 'completed') {
              clearInterval(pollInterval);
              setUploadingPlatform(null);
              setRecording(prev => ({ ...prev, videoUpload: status.videoUpload }));
              toast.success(`Uploaded to ${platform === 'youtube' ? 'YouTube' : 'Vimeo'}!`);
            } else if (upload?.status === 'failed') {
              clearInterval(pollInterval);
              setUploadingPlatform(null);
              toast.error(`Upload failed: ${upload.error || 'Unknown error'}`);
            }
          } catch { /* ignore polling errors */ }
        }, 5000);
        // Clear poll after 10 minutes max
        setTimeout(() => clearInterval(pollInterval), 600000);
      }
    } catch (err) {
      setUploadingPlatform(null);
      toast.error(`Upload failed: ${err.message || 'Unknown error'}`);
    }
  };

  const triggerNotesGeneration = async () => {
    try {
      setNotesGenerating(true);
      const result = await generateNotesOnDemand(recordingId);
      if (result?.recording) {
        setRecording(result.recording);
      }
      if (result?.notes) {
        setNotes(result.notes);
        toast.success('AI notes generated successfully');
      }
    } catch (error) {
      console.error('Failed to generate notes:', error);
    } finally {
      setNotesGenerating(false);
    }
  };

  const loadRecordingData = async () => {
    try {
      setLoading(true);
      const [recData, transData, bookData, notesData, chatData] = await Promise.all([
        getRecording(recordingId),
        getTranscript(recordingId).catch(() => ({ segments: [] })),
        getBookmarks(recordingId).catch(() => ({ bookmarks: [] })),
        getRecordingNotes(recordingId).catch(() => ({ notes: null })),
        getRecordingChat(recordingId).catch(() => ({ messages: [] })),
      ]);

      const rec = recData?.recording;
      setRecording(rec);
      setTranscript(transData?.segments || []);
      setBookmarks(bookData?.bookmarks || []);
      setNotes(notesData?.notes || null);
      setChatMessages(chatData?.messages || []);

      // Load recording assets if multi-layout recording
      // (multi-layout-web stamps rawRecordingMode='web' but is multi-layout via recorderConfig.mode)
      if (
        rec?.rawRecordingMode === 'individual'
        || rec?.rawRecordingMode === 'selfhosted'
        || rec?.recorderConfig?.mode === 'multi-layout-web'
      ) {
        try {
          const assetsData = await fetchRecordingAssets(recordingId);
          const assets = assetsData?.assets || [];
          setRecordingAssets(assets);
          setLayoutProcessingStatus(rec.layoutProcessingStatus || null);
          // Pick the best default layout. Pertrack mirrors take precedence
          // over their legacy Xvfb-captured siblings: the latter truncates to
          // ~25% of the capture window, the pertrack composer captures the
          // full window. Falls through to legacy if pertrack isn't there.
          const pickDefault = () => {
            if (rec.primaryAssetType) {
              const pertrackEquivalent = `${rec.primaryAssetType}_pertrack`;
              const pertrackAsset = assets.find(x => x.assetType === pertrackEquivalent && x.processingStatus === 'completed');
              if (pertrackAsset) return pertrackEquivalent;
              const a = assets.find(x => x.assetType === rec.primaryAssetType && x.processingStatus === 'completed');
              if (a) return rec.primaryAssetType;
            }
            const preferred = [
              'gallery_view_pertrack', 'focus_pertrack', 'speaker_top_pertrack', 'speaker_side_pertrack',
              'gallery_view',          'focus',          'speaker_top',          'speaker_side',
            ];
            for (const t of preferred) {
              if (assets.find(x => x.assetType === t && x.processingStatus === 'completed')) return t;
            }
            const first = assets.find(x => x.format === 'mp4' || x.format === 'webm');
            return first?.assetType || null;
          };
          const def = pickDefault();
          if (def) setActiveLayoutType(def);
        } catch (err) {
          console.error('Failed to load recording assets:', err);
        }
      }

      // Fetch session siblings: try sessionGroupId first, then sessionId
      const lookupIds = [rec?.sessionGroupId, rec?.sessionId].filter(Boolean);
      let foundSiblings = false;
      for (const lookupId of lookupIds) {
        if (foundSiblings) break;
        try {
          const groupData = await getSessionGroupRecordings(lookupId);
          const siblings = (groupData?.recordings || []).filter(
            r => r.recordingType === 'agora_cloud' || r.recordingType === 'puppeteer'
          );
          if (siblings.length > 1) {
            siblings.sort((a, b) => (a.recordingIndex || 0) - (b.recordingIndex || 0));
            setSessionRecordings(siblings);
            const currentIdx = siblings.findIndex(r => r.id === recordingId);
            setActiveRecordingIndex(currentIdx >= 0 ? currentIdx : 0);
            foundSiblings = true;
          }
        } catch (err) {
          console.error('Failed to load session group:', err);
        }
      }
      if (!foundSiblings) {
        setSessionRecordings([]);
      }
    } catch (error) {
      console.error('Failed to load recording:', error);
      toast.error('Failed to load recording');
    } finally {
      setLoading(false);
    }
  };

  const handleProgress = (state) => {
    setCurrentTimeMs(state.playedSeconds * 1000);
  };

  const handleSeek = (timeMs) => {
    if (playerRef.current) {
      playerRef.current.seekTo(timeMs / 1000, 'seconds');
    }
  };

  const startEditingTitle = () => {
    setEditTitle(recording.name || '');
    setIsEditingTitle(true);
    setTimeout(() => titleInputRef.current?.focus(), 0);
  };

  const cancelEditingTitle = () => {
    setIsEditingTitle(false);
    setEditTitle('');
  };

  const saveTitle = async () => {
    const trimmed = editTitle.trim();
    if (!trimmed || trimmed === recording.name) {
      cancelEditingTitle();
      return;
    }
    setSavingTitle(true);
    try {
      await updateMeetingRecordingName({ recordingId: recording.fileId || recordingId, name: trimmed });
      setRecording(prev => ({ ...prev, name: trimmed }));
      setIsEditingTitle(false);
      toast.success('Recording renamed');
    } catch (err) {
      console.error('Failed to rename recording:', err);
      toast.error('Failed to rename recording');
    } finally {
      setSavingTitle(false);
    }
  };

  const handleTitleKeyDown = (e) => {
    if (e.key === 'Enter') saveTitle();
    if (e.key === 'Escape') cancelEditingTitle();
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      // Multi-part session: delete every recording in the session group, not just
      // the one in the URL. Otherwise users hit Delete and the other parts stay
      // visible in the recordings list.
      const partIds = sessionRecordings.length > 1
        ? sessionRecordings.map((r) => r.id || r._id).filter(Boolean)
        : [recordingId];

      const results = await Promise.allSettled(
        partIds.map((id) => deleteRecording(id))
      );

      const failed = results.filter((r) => r.status === 'rejected');
      if (failed.length === 0) {
        toast.success(
          partIds.length > 1
            ? `Deleted ${partIds.length} recording parts`
            : 'Recording deleted'
        );
        navigate('/dashboard/recordings');
      } else if (failed.length < partIds.length) {
        console.error('Partial delete failure:', failed.map((f) => f.reason?.message || f.reason));
        toast.warning(`Deleted ${partIds.length - failed.length} of ${partIds.length} parts; ${failed.length} failed`);
        navigate('/dashboard/recordings');
      } else {
        throw failed[0].reason;
      }
    } catch (err) {
      console.error('Failed to delete recording:', err);
      toast.error('Failed to delete recording');
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleBookmarkClick = (bookmark) => {
    handleSeek(bookmark.timestampMs);
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#001233]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00c853]"></div>
      </div>
    );
  }

  if (!recording) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-[#001233] text-white">
        <p className="text-xl mb-4">Recording not found</p>
        <button
          onClick={() => navigate('/dashboard/recordings')}
          className="px-4 py-2 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] rounded-lg"
        >
          Back to Recordings
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#001233] text-white">
      {/* Header */}
      <div className="border-b border-white/10 bg-[#001233]/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard/recordings')}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                {isEditingTitle ? (
                  <div className="flex items-center gap-2">
                    <input
                      ref={titleInputRef}
                      type="text"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      onKeyDown={handleTitleKeyDown}
                      onBlur={saveTitle}
                      disabled={savingTitle}
                      className="text-xl font-semibold bg-white/10 border border-white/20 rounded-lg px-2 py-1 text-white outline-none focus:border-[#6a1fbf]/60 w-80"
                    />
                    <button onClick={saveTitle} disabled={savingTitle} className="p-1 hover:bg-white/10 rounded-lg text-[#00c853] transition-colors">
                      <Check className="w-4 h-4" />
                    </button>
                    <button onClick={cancelEditingTitle} onMouseDown={(e) => e.preventDefault()} className="p-1 hover:bg-white/10 rounded-lg text-white/50 transition-colors">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <h1
                    className="text-xl font-semibold flex items-center gap-2 group cursor-pointer"
                    onClick={startEditingTitle}
                    title="Click to rename"
                  >
                    {recording.name}
                    <Pencil className="w-3.5 h-3.5 text-white/30 group-hover:text-white/70 transition-colors" />
                    {sessionRecordings.length > 1 && (
                      <span className="text-sm font-normal text-white/50">
                        ({sessionRecordings.length} parts)
                      </span>
                    )}
                    {pipelineState === 'recording' && (
                      <span className="flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 border border-red-500/30">
                        <span className="relative flex h-1.5 w-1.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500" />
                        </span>
                        Recording
                      </span>
                    )}
                    {pipelineState === 'processing' && (
                      <span className="flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        Processing
                      </span>
                    )}
                    {pipelineState === 'failed' && (
                      <span className="flex items-center gap-1.5 text-[11px] font-medium px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 border border-red-500/30">
                        Failed
                      </span>
                    )}
                  </h1>
                )}
                <div className="flex items-center gap-4 mt-1 text-sm text-white/60">
                  <span className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {formatDuration(
                      sessionRecordings.length > 1
                        ? sessionRecordings.reduce((sum, r) => sum + (r.duration || 0), 0)
                        : recording.duration
                    )}
                  </span>
                  <span className="relative">
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowParticipants(!showParticipants); }}
                      className="flex items-center gap-1 hover:text-white transition-colors cursor-pointer"
                    >
                      <Users className="w-4 h-4" />
                      {(() => {
                        const pList = recording.participants?.length > 0
                          ? recording.participants
                          : [...new Set(transcript.map(s => s.speakerName || s.speaker).filter(Boolean))];
                        return `${pList.length} participant${pList.length !== 1 ? 's' : ''}`;
                      })()}
                    </button>
                    {showParticipants && (
                      <ParticipantsPopup
                        participants={
                          recording.participants?.length > 0
                            ? recording.participants
                            : [...new Set(transcript.map(s => s.speakerName || s.speaker).filter(Boolean))]
                        }
                        onClose={() => setShowParticipants(false)}
                      />
                    )}
                  </span>
                  <span>{new Date(recording.createdAt).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true, timeZoneName: 'short' })}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowShareModal(true)}
                className="px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg transition-colors flex items-center gap-2"
              >
                <Share2 className="w-4 h-4" />
                Share
              </button>
              {(activeRecording?.fileUrl || activeRecording?.s3VideoUrl) && (
                <div className="relative">
                  {sessionRecordings.length > 1 ? (
                    <>
                      <button
                        onClick={() => !videoDownloading && setShowVideoDownloadMenu(!showVideoDownloadMenu)}
                        disabled={videoDownloading}
                        className="px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                      >
                        {videoDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                        {videoDownloading ? 'Downloading...' : 'Video'}
                        {!videoDownloading && <ChevronDown className="w-3 h-3" />}
                      </button>
                      {showVideoDownloadMenu && (
                        <>
                          <div className="fixed inset-0 z-20" onClick={() => setShowVideoDownloadMenu(false)} />
                          <div className="absolute right-0 mt-2 w-52 bg-[#0D1117] border border-white/20 rounded-lg shadow-xl z-30 overflow-hidden">
                            {sessionRecordings.map((rec, idx) => {
                              if (!rec.fileUrl && !rec.s3VideoUrl) return null;
                              return (
                                <button
                                  key={rec._id || rec.id}
                                  onClick={() => { setShowVideoDownloadMenu(false); handleDownloadVideo(rec); }}
                                  className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-white/10 flex items-center gap-2 transition-colors"
                                >
                                  <Film className="w-4 h-4 text-purple-400" />
                                  Part {idx + 1}
                                  {rec.duration > 0 && (
                                    <span className="text-xs text-white/40 ml-auto">{formatDuration(rec.duration)}</span>
                                  )}
                                </button>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </>
                  ) : (
                    <button
                      onClick={() => handleDownloadVideo()}
                      disabled={videoDownloading}
                      className="px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                      {videoDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                      {videoDownloading ? 'Downloading...' : 'Video'}
                    </button>
                  )}
                </div>
              )}
              {/* Download Notes/Transcript dropdown */}
              <div className="relative">
                <button
                  onClick={() => {
                    // When opening the dropdown, align its part selector to the
                    // part the user is currently viewing — they probably want
                    // that one's assets first but can switch via the <select>.
                    if (!showDownloadMenu) setExportSelectedPartIdx(activeRecordingIndex);
                    setShowDownloadMenu(!showDownloadMenu);
                  }}
                  className="px-4 py-2 bg-gradient-to-r from-[#6a1fbf] to-[#00c853] hover:opacity-90 rounded-lg transition-opacity flex items-center gap-2"
                >
                  <FileDown className="w-4 h-4" />
                  Export
                </button>
                {showDownloadMenu && (() => {
                  // Multi-part recordings: show assets for whichever part the
                  // Export dropdown's <select> currently points at. The backend
                  // /session-group endpoint returns each part with its own
                  // assets[] array, so we render that part's list + route each
                  // download URL to the correct part's recordingId.
                  // Single-part recordings: just use the existing recordingId +
                  // recordingAssets as before — no <select> shown.
                  const hasMultipleParts = sessionRecordings.length > 1;
                  const activePart = hasMultipleParts
                    ? sessionRecordings[Math.min(exportSelectedPartIdx, sessionRecordings.length - 1)]
                    : null;
                  const partRecordingId = activePart ? (activePart._id || activePart.id) : recordingId;
                  const partAssets = activePart?.assets?.length
                    ? activePart.assets.map(a => ({ ...a, processingStatus: 'completed' }))
                    : recordingAssets;
                  return (
                  <>
                    <div className="fixed inset-0 z-20" onClick={() => setShowDownloadMenu(false)} />
                    <div className="absolute right-0 mt-2 w-64 bg-[#001233] border border-white/20 rounded-lg shadow-xl z-30 overflow-hidden max-h-[70vh] overflow-y-auto">
                      {/* Part selector — only shown for multi-part recordings */}
                      {hasMultipleParts && (
                        <div className="px-4 py-3 border-b border-white/10 bg-white/5">
                          <label className="block text-[10px] font-semibold text-white/50 uppercase tracking-wider mb-1.5">
                            Recording Part
                          </label>
                          <select
                            value={exportSelectedPartIdx}
                            onChange={(e) => setExportSelectedPartIdx(Number(e.target.value))}
                            className="w-full px-2 py-1.5 text-sm bg-[#001233] border border-white/20 rounded text-white focus:outline-none focus:border-purple-400"
                          >
                            {sessionRecordings.map((rec, idx) => {
                              const dur = rec.duration || 0;
                              const mins = Math.floor(dur / 60);
                              const secs = dur % 60;
                              const durStr = dur >= 60 ? `${mins}m ${secs}s` : `${dur}s`;
                              return (
                                <option key={rec._id || rec.id || idx} value={idx}>
                                  Part {idx + 1} ({durStr})
                                  {idx === activeRecordingIndex ? ' — currently viewing' : ''}
                                </option>
                              );
                            })}
                          </select>
                        </div>
                      )}
                      {/* Multi-layout video downloads */}
                      {isMultiLayout && (() => {
                        const allVideo = partAssets.filter(
                          a => a.format === 'mp4' && a.processingStatus === 'completed'
                        );
                        // Collapse legacy + pertrack pairs into one entry.
                        // The pertrack file is the source of truth (legacy
                        // Xvfb captures get truncated); if a pertrack mirror
                        // exists we hide the legacy sibling from downloads.
                        const pertrackBaseTypes = new Set(
                          allVideo
                            .filter(a => a.assetType.endsWith('_pertrack'))
                            .map(a => a.assetType.replace(/_pertrack$/, ''))
                        );
                        const videoAssets = allVideo.filter(a => !pertrackBaseTypes.has(a.assetType));
                        if (videoAssets.length === 0) return null;
                        const layoutLabels = {
                          selfhosted_composited: 'Full Stage',
                          gallery_view: 'Gallery',
                          gallery_view_pertrack: 'Gallery',
                          focus: 'Focus',
                          focus_pertrack: 'Focus',
                          speaker_top: 'Speaker Top',
                          speaker_top_pertrack: 'Speaker Top',
                          speaker_side: 'Speaker Side',
                          speaker_side_pertrack: 'Speaker Side',
                          screen_speaker: 'Screen + Speaker',
                          screen_gallery: 'Screen + Gallery',
                          screen_only: 'Screen Only',
                        };
                        return (
                          <>
                            <div className="px-4 py-2 text-xs font-semibold text-white/40 uppercase tracking-wider border-b border-white/10">
                              Video Layouts
                            </div>
                            {videoAssets.map(asset => (
                              <a
                                key={asset.id}
                                href={getAssetDownloadUrl(partRecordingId, asset.id)}
                                className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-white/10 flex items-center gap-2 transition-colors"
                                onClick={() => setShowDownloadMenu(false)}
                              >
                                <Film className="w-4 h-4 text-purple-400" />
                                {layoutLabels[asset.assetType] || asset.assetType}
                                <span className="ml-auto text-xs text-white/30">
                                  {asset.fileSize ? `${(asset.fileSize / 1024 / 1024).toFixed(0)}MB` : '.mp4'}
                                </span>
                              </a>
                            ))}
                          </>
                        );
                      })()}

                      {/* Audio downloads */}
                      {isMultiLayout && (() => {
                        const audioOnly = partAssets.find(
                          a => a.assetType === 'audio_only' && a.processingStatus === 'completed'
                        );
                        const participantAudios = partAssets.filter(
                          a => a.assetType === 'participant_audio' && a.processingStatus === 'completed'
                        );
                        if (!audioOnly && participantAudios.length === 0) return null;
                        return (
                          <>
                            <div className="px-4 py-2 text-xs font-semibold text-white/40 uppercase tracking-wider border-b border-white/10">
                              Audio
                            </div>
                            {audioOnly && (
                              <a
                                href={getAssetDownloadUrl(partRecordingId, audioOnly.id)}
                                className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-white/10 flex items-center gap-2 transition-colors"
                                onClick={() => setShowDownloadMenu(false)}
                              >
                                <Download className="w-4 h-4 text-blue-400" />
                                Audio Only
                                <span className="ml-auto text-xs text-white/30">.mp3</span>
                              </a>
                            )}
                            {participantAudios.length > 0 && (
                              <>
                                <div className="px-4 py-1.5 text-xs text-white/30">Per-Participant Audio</div>
                                {participantAudios.map(asset => (
                                  <a
                                    key={asset.id}
                                    href={getAssetDownloadUrl(partRecordingId, asset.id)}
                                    className="w-full px-4 py-2 text-left text-sm text-white hover:bg-white/10 flex items-center gap-2 transition-colors pl-6"
                                    onClick={() => setShowDownloadMenu(false)}
                                  >
                                    <span className="text-xs">🎵</span>
                                    {asset.participantName || `Participant ${asset.participantUid}`}
                                    <span className="ml-auto text-xs text-white/30">.mp3</span>
                                  </a>
                                ))}
                              </>
                            )}
                          </>
                        );
                      })()}

                      {/* Text downloads section header for multi-layout */}
                      {isMultiLayout && (notes || recording.summary || transcript.length > 0 || windowChatMessages.length > 0) && (
                        <div className="px-4 py-2 text-xs font-semibold text-white/40 uppercase tracking-wider border-b border-white/10">
                          Text
                        </div>
                      )}

                      {/* Chat & Caption asset downloads for multi-layout */}
                      {isMultiLayout && (() => {
                        const chatTxt = partAssets.find(
                          a => a.assetType === 'chat_txt' && a.processingStatus === 'completed'
                        );
                        const captionsTxt = partAssets.find(
                          a => a.assetType === 'captions_txt' && a.processingStatus === 'completed'
                        );
                        return (
                          <>
                            {chatTxt && (
                              <a
                                href={getAssetDownloadUrl(partRecordingId, chatTxt.id)}
                                className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-white/10 flex items-center gap-2 transition-colors"
                                onClick={() => setShowDownloadMenu(false)}
                              >
                                <MessageSquare className="w-4 h-4 text-emerald-400" />
                                Chat File
                                <span className="ml-auto text-xs text-white/30">.txt</span>
                              </a>
                            )}
                            {captionsTxt && (
                              <a
                                href={getAssetDownloadUrl(partRecordingId, captionsTxt.id)}
                                className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-white/10 flex items-center gap-2 transition-colors"
                                onClick={() => setShowDownloadMenu(false)}
                              >
                                <ClipboardList className="w-4 h-4 text-yellow-400" />
                                Closed Captions
                                <span className="ml-auto text-xs text-white/30">.txt</span>
                              </a>
                            )}
                          </>
                        );
                      })()}

                      {/* Standard downloads (always available) */}
                      {(notes || recording.summary) && (
                        <button
                          onClick={handleDownloadNotes}
                          className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-white/10 flex items-center gap-2 transition-colors"
                        >
                          <FileText className="w-4 h-4 text-[#00c853]" />
                          AI Notes
                        </button>
                      )}
                      {!isMultiLayout && transcript.length > 0 && (
                        <button
                          onClick={handleDownloadTranscript}
                          className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-white/10 flex items-center gap-2 transition-colors"
                        >
                          <ClipboardList className="w-4 h-4 text-blue-400" />
                          Transcript
                        </button>
                      )}
                      {!isMultiLayout && windowChatMessages.length > 0 && (
                        <button
                          onClick={handleDownloadChat}
                          className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-white/10 flex items-center gap-2 transition-colors"
                        >
                          <MessageSquare className="w-4 h-4 text-emerald-400" />
                          Chat Log
                        </button>
                      )}
                      {!isMultiLayout && !notes && !recording.summary && transcript.length === 0 && windowChatMessages.length === 0 && (
                        <div className="px-4 py-2.5 text-sm text-white/40">No data to export</div>
                      )}
                    </div>
                  </>
                  );
                })()}
              </div>
              {/* Upload to YouTube/Vimeo */}
              {videoIntegration?.enabled && (activeRecording?.fileUrl || activeRecording?.s3VideoUrl) && (
                <div className="relative">
                  {uploadingPlatform ? (
                    <button disabled className="px-4 py-2 bg-white/10 border border-white/20 rounded-lg flex items-center gap-2 opacity-60">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Uploading to {uploadingPlatform === 'youtube' ? 'YouTube' : 'Vimeo'}...
                    </button>
                  ) : recording?.videoUpload?.youtube?.status === 'completed' || recording?.videoUpload?.vimeo?.status === 'completed' ? (
                    <div className="flex items-center gap-1">
                      {recording.videoUpload?.youtube?.status === 'completed' && (
                        <a href={recording.videoUpload.youtube.videoUrl} target="_blank" rel="noopener noreferrer"
                          className="px-3 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 rounded-lg flex items-center gap-2 text-sm text-red-400 transition-colors">
                          <Youtube className="w-4 h-4" /> YouTube
                          <ExternalLink className="w-3 h-3" />
                        </a>
                      )}
                      {recording.videoUpload?.vimeo?.status === 'completed' && (
                        <a href={recording.videoUpload.vimeo.videoUrl} target="_blank" rel="noopener noreferrer"
                          className="px-3 py-2 bg-blue-500/10 hover:bg-blue-500/20 border border-blue-500/20 rounded-lg flex items-center gap-2 text-sm text-blue-400 transition-colors">
                          <ExternalLink className="w-3 h-3" /> Vimeo
                        </a>
                      )}
                      <button onClick={() => setShowUploadMenu(!showUploadMenu)}
                        className="p-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg transition-colors">
                        <Upload className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowUploadMenu(!showUploadMenu)}
                      className="px-4 py-2 bg-white/10 hover:bg-white/20 border border-white/20 rounded-lg transition-colors flex items-center gap-2"
                    >
                      <Upload className="w-4 h-4" />
                      Upload
                      <ChevronDown className="w-3 h-3" />
                    </button>
                  )}
                  {showUploadMenu && (
                    <>
                      <div className="fixed inset-0 z-20" onClick={() => setShowUploadMenu(false)} />
                      <div className="absolute right-0 mt-2 w-48 bg-[#001233] border border-white/20 rounded-lg shadow-xl z-30 overflow-hidden">
                        {videoIntegration.youtube?.connected && (
                          <button
                            onClick={() => handleUploadToPlatform('youtube')}
                            className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-white/10 flex items-center gap-2 transition-colors"
                          >
                            <Youtube className="w-4 h-4 text-red-500" />
                            Upload to YouTube
                            {recording?.videoUpload?.youtube?.status === 'failed' && (
                              <span className="text-xs text-red-400 ml-auto">Retry</span>
                            )}
                          </button>
                        )}
                        {videoIntegration.vimeo?.connected && (
                          <button
                            onClick={() => handleUploadToPlatform('vimeo')}
                            className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-white/10 flex items-center gap-2 transition-colors"
                          >
                            <svg className="w-4 h-4 text-blue-400" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M23.977 6.416c-.105 2.338-1.739 5.543-4.894 9.609-3.268 4.247-6.026 6.37-8.29 6.37-1.409 0-2.578-1.294-3.553-3.881L5.322 11.4C4.603 8.816 3.834 7.522 3.01 7.522c-.179 0-.806.378-1.881 1.132L0 7.197a315.065 315.065 0 0 0 3.501-3.123C5.08 2.701 6.266 1.984 7.055 1.91c1.867-.18 3.016 1.1 3.447 3.838.465 2.953.789 4.789.971 5.507.539 2.45 1.131 3.674 1.776 3.674.502 0 1.256-.796 2.265-2.385 1.004-1.589 1.54-2.797 1.612-3.628.144-1.371-.395-2.061-1.614-2.061-.574 0-1.167.121-1.777.391 1.186-3.868 3.434-5.757 6.762-5.637 2.473.06 3.628 1.664 3.493 4.797l-.013.01z"/>
                            </svg>
                            Upload to Vimeo
                            {recording?.videoUpload?.vimeo?.status === 'failed' && (
                              <span className="text-xs text-red-400 ml-auto">Retry</span>
                            )}
                          </button>
                        )}
                        {!videoIntegration.youtube?.connected && !videoIntegration.vimeo?.connected && (
                          <div className="px-4 py-2.5 text-sm text-white/40">No platforms connected. Go to Integrations to connect.</div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="p-2 hover:bg-red-500/20 border border-white/10 hover:border-red-500/30 rounded-lg transition-colors text-white/50 hover:text-red-400"
                title="Delete recording"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Pipeline status banner — reassures users that their recording was
            captured and is still being prepared. Hidden once pipelineState is
            'ready' so it never clutters a finished page. */}
        {(pipelineState === 'recording' || pipelineState === 'processing') && (
          <div
            className={`mb-5 rounded-2xl p-4 flex items-start gap-3 border ${
              pipelineState === 'recording'
                ? 'bg-red-500/10 border-red-500/30'
                : 'bg-amber-500/10 border-amber-500/30'
            }`}
          >
            {pipelineState === 'recording' ? (
              <span className="relative flex h-3 w-3 mt-1.5 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
              </span>
            ) : (
              <Loader2 className="w-5 h-5 text-amber-400 animate-spin shrink-0 mt-0.5" />
            )}
            <div className="flex-1 min-w-0">
              <div className="text-white font-semibold text-sm">
                {pipelineInfo.phaseTitle}
              </div>
              <div className="text-white/60 text-xs mt-0.5">
                {pipelineInfo.phaseDetail}
              </div>
            </div>
          </div>
        )}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column: Video + Summary */}
          <div className="lg:col-span-2 space-y-6">
            {/* Recording Part Tabs (only shown for multi-recording sessions) */}
            {sessionRecordings.length > 1 && (
              <div className="flex items-center gap-1 border-b border-white/10 pb-2">
                {sessionRecordings.map((rec, idx) => (
                  <button
                    key={rec.id}
                    onClick={() => setActiveRecordingIndex(idx)}
                    className={`px-3 py-1.5 rounded-t-lg text-sm font-medium transition-colors flex items-center gap-1.5 ${
                      idx === activeRecordingIndex
                        ? 'bg-[#6a1fbf]/20 text-white border border-[#6a1fbf]/30 border-b-0'
                        : 'text-white/50 hover:text-white/70 hover:bg-white/5'
                    }`}
                  >
                    <Film className={`w-3.5 h-3.5 ${rec.status === 'failed' ? 'text-red-400' : ''}`} />
                    Part {idx + 1}
                    {rec.status === 'failed' ? (
                      <span className="text-[10px] text-red-400">(failed)</span>
                    ) : (
                      <span className="text-[10px] text-white/40">
                        ({formatDuration(rec.duration || 0)})
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Layout Selector — only show for multi-layout recordings */}
            {isMultiLayout && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 flex-wrap">
                  {[
                    { type: 'gallery_view', label: 'Gallery', icon: '◫' },
                    { type: 'focus', label: 'Focus', icon: '▢' },
                    { type: 'speaker_top', label: 'Speaker Top', icon: '▬' },
                    { type: 'speaker_side', label: 'Speaker Side', icon: '◧' },
                    { type: 'screen_speaker', label: 'Screen + Speaker', icon: '◨', needsScreenShare: true },
                    { type: 'screen_gallery', label: 'Screen + Gallery', icon: '⊞', needsScreenShare: true },
                    { type: 'screen_only', label: 'Screen Only', icon: '▣', needsScreenShare: true },
                  ].filter(layout => {
                    if (layout.needsScreenShare && !recording?.hasScreenShare) return false;
                    return true;
                  }).map(layout => {
                    // Prefer the pertrack mirror when it exists — the legacy
                    // Xvfb capture truncates badly. Each button still says
                    // "Gallery" / "Focus" / etc. but resolves to the full
                    // pertrack asset under the hood.
                    const pertrackType = `${layout.type}_pertrack`;
                    const pertrackAsset = recordingAssets.find(a => a.assetType === pertrackType);
                    const legacyAsset = recordingAssets.find(a => a.assetType === layout.type);
                    const asset = (pertrackAsset?.processingStatus === 'completed' ? pertrackAsset : legacyAsset);
                    const resolvedType = asset?.assetType || layout.type;
                    const isReady = asset?.processingStatus === 'completed';
                    const isProcessing = asset?.processingStatus === 'processing' || asset?.processingStatus === 'pending';
                    const isActive = activeLayoutType === resolvedType;

                    return (
                      <button
                        key={layout.type}
                        onClick={() => isReady && setActiveLayoutType(resolvedType)}
                        disabled={!isReady}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
                          isActive
                            ? 'bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white shadow-lg'
                            : isReady
                              ? 'bg-white/10 text-white/80 hover:bg-white/20 border border-white/10'
                              : 'bg-white/5 text-white/30 border border-white/5 cursor-not-allowed'
                        }`}
                      >
                        <span className="text-xs">{layout.icon}</span>
                        {layout.label}
                        {isProcessing && <Loader2 className="w-3 h-3 animate-spin ml-1" />}
                      </button>
                    );
                  })}
                </div>
                {layoutProcessingStatus === 'processing' && (
                  <div className="flex items-center gap-2 text-xs text-white/40">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Processing recording layouts...
                  </div>
                )}
              </div>
            )}

            {/* Video Player — only show if there's a video URL and recording didn't fail */}
            {activeRecording?.status === 'failed' || videoError ? (
              <div className="bg-white/5 backdrop-blur-xl border border-red-500/20 rounded-2xl p-8 text-center">
                <Film className="w-12 h-12 text-red-400 mx-auto mb-3" />
                <h3 className="text-lg font-semibold mb-1">Video Unavailable</h3>
                <p className="text-sm text-white/50">
                  {activeRecording?.status === 'failed'
                    ? 'This recording failed to process. The video file was not saved successfully.'
                    : 'The video file could not be loaded. It may have been removed or failed to upload.'}
                </p>
              </div>
            ) : (activeVideoUrl || activeRecording?.fileUrl || activeRecording?.s3VideoUrl) ? (
              <>
                {/* C3 — Detected-resolution warning. Surfaces when the worker's
                    capture-start sample shows any participant arrived below
                    720p, which is the recorder's view of the publisher's
                    actual upstream — not the dashboard's playback resolution.
                    Lets users know their recording is blurry because of the
                    publisher's network at the time, not the recording pipeline. */}
                {(() => {
                  const dr = activeRecording?.detectedResolutions;
                  if (!dr || typeof dr !== 'object') return null;
                  const constrained = Object.entries(dr)
                    .filter(([k]) => k !== 'sampledAt' && k !== '__error')
                    .map(([uid, s]) => ({ uid, ...(s || {}) }))
                    .filter(s => Number.isFinite(s.width) && s.width > 0 && s.width < 1280);
                  if (constrained.length === 0) return null;
                  const worst = constrained.reduce((a, b) => (a.width <= b.width ? a : b));
                  return (
                    <div className="mb-3 bg-yellow-400/5 border border-yellow-400/20 rounded-2xl p-3 flex items-start gap-3">
                      <Film className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                      <div className="text-xs text-white/80">
                        <div className="font-medium text-yellow-200 mb-0.5">Source resolution was constrained</div>
                        <div className="text-white/60">
                          One or more participants published at {worst.width}×{worst.height || '?'} during this recording — the recorder captured what was sent.
                          Likely cause: the publisher's network was throttling Agora's adaptive bitrate at the time.
                        </div>
                      </div>
                    </div>
                  );
                })()}
                <div className="bg-black rounded-2xl overflow-hidden">
                  <ReactPlayer
                    key={`${activeRecording?.id || recordingId}-${activeLayoutType || 'default'}`}
                    ref={playerRef}
                    url={activeVideoUrl}
                    width="100%"
                    height="auto"
                    controls
                    onProgress={handleProgress}
                    onError={() => setVideoError(true)}
                    config={{
                      file: {
                        attributes: {
                          controlsList: 'nodownload',
                        },
                      },
                    }}
                  />
                </div>

                {/* Anchor that owns the smooth-scroll target. Always rendered
                    so we can scroll on demand; only the editor below toggles. */}
                <div ref={clipEditorAnchorRef} />

                {/* Inline Clip editor — appears below the player when the user
                    clicks "+ Create clip" from the Clips section below. */}
                {clipEditorOpen && (
                  <ClipEditor
                    parentRecordingId={activeRecording?._id || activeRecording?.id || recordingId}
                    durationMs={(activeRecording?.duration || 0) * 1000}
                    currentTimeMs={currentTimeMs}
                    availableLayouts={
                      Array.isArray(activeRecording?.availableLayouts) && activeRecording.availableLayouts.length > 0
                        ? activeRecording.availableLayouts
                        : (recordingAssets || []).map((a) => a.assetType)
                    }
                    // Pass the per-layout asset list so the editor's dedicated
                    // preview pane can resolve the right URL for whichever
                    // layout the user picked (focus / gallery / audio / etc).
                    recordingAssets={recordingAssets || []}
                    fallbackVideoUrl={activeVideoUrl}
                    onSeek={handleSeek}
                    onClose={() => setClipEditorOpen(false)}
                    onSaved={() => {
                      setClipEditorOpen(false);
                      setClipsRefreshKey((k) => k + 1);
                    }}
                  />
                )}

                {/* Clips library — sits below the player + editor in the main
                    column so it's always visible alongside the recording. */}
                <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <Film className="w-4 h-4 text-[#a677d9]" />
                    <h3 className="text-sm font-semibold text-white">Clips</h3>
                  </div>
                  <ClipsPanel
                    key={`${activeRecording?._id || activeRecording?.id || recordingId}-${clipsRefreshKey}`}
                    recordingId={activeRecording?._id || activeRecording?.id || recordingId}
                    refreshKey={clipsRefreshKey}
                    onCreateClick={() => {
                      setClipEditorOpen(true);
                      // Wait for the editor to mount, then smooth-scroll it
                      // into view. requestAnimationFrame batches with React's
                      // commit phase so the ref is current.
                      requestAnimationFrame(() => {
                        const node = clipEditorAnchorRef.current;
                        if (node?.scrollIntoView) {
                          node.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                      });
                    }}
                    onPlayClip={(clip) => setPlayingClip(clip)}
                    onShareClip={async (clip) => {
                      const baseUrl = window.location.origin;
                      const url = `${baseUrl}/clip-preview/${clip.shareToken}`;
                      let ok = false;
                      try {
                        if (navigator.clipboard?.writeText) {
                          await navigator.clipboard.writeText(url);
                          ok = true;
                        }
                      } catch (_) { /* fall through */ }
                      if (!ok) {
                        try {
                          const ta = document.createElement('textarea');
                          ta.value = url;
                          ta.style.position = 'fixed';
                          ta.style.opacity = '0';
                          document.body.appendChild(ta);
                          ta.focus(); ta.select();
                          ok = document.execCommand('copy');
                          document.body.removeChild(ta);
                        } catch (_) { /* ignore */ }
                      }
                      if (ok) {
                        toast.success('Share link copied to clipboard');
                      } else {
                        toast.error('Couldn\'t copy. Link: ' + url, { autoClose: 8000 });
                      }
                    }}
                  />
                </div>
              </>
            ) : pipelineState === 'recording' ? (
              <div className="bg-white/5 backdrop-blur-xl border border-red-500/20 rounded-2xl p-8 text-center">
                <span className="relative inline-flex h-12 w-12 mx-auto mb-3 items-center justify-center">
                  <span className="animate-ping absolute inline-flex h-8 w-8 rounded-full bg-red-400 opacity-75" />
                  <span className="relative inline-flex rounded-full h-6 w-6 bg-red-500" />
                </span>
                <h3 className="text-lg font-semibold mb-1">{pipelineInfo.phaseTitle}</h3>
                <p className="text-sm text-white/50">{pipelineInfo.phaseDetail}</p>
              </div>
            ) : pipelineState === 'processing' ? (
              <div className="bg-white/5 backdrop-blur-xl border border-amber-500/20 rounded-2xl p-8 text-center">
                <Loader2 className="w-12 h-12 text-amber-400 mx-auto mb-3 animate-spin" />
                <h3 className="text-lg font-semibold mb-1">{pipelineInfo.phaseTitle}</h3>
                <p className="text-sm text-white/50">{pipelineInfo.phaseDetail}</p>
              </div>
            ) : (
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 text-center">
                <FileText className="w-12 h-12 text-purple-400 mx-auto mb-3" />
                <h3 className="text-lg font-semibold mb-1">Meeting Notes</h3>
                <p className="text-sm text-white/50">This meeting has transcript and AI notes but no video recording</p>
              </div>
            )}

            {/* Recording Files (Zoom-style list) — for multi-layout recordings */}
            {isMultiLayout && recordingAssets.length > 0 && (() => {
              const formatBytes = (bytes) => {
                if (!bytes) return '—';
                if (bytes < 1024) return `${bytes} B`;
                if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
                if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(0)} MB`;
                return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
              };
              const videoLayouts = [
                { type: 'gallery_view', label: 'Gallery', icon: Film },
                { type: 'focus', label: 'Focus', icon: Film },
                { type: 'speaker_top', label: 'Speaker Top', icon: Film },
                { type: 'speaker_side', label: 'Speaker Side', icon: Film },
                { type: 'screen_speaker', label: 'Screen + Speaker', icon: Film, needsScreenShare: true },
                { type: 'screen_gallery', label: 'Screen + Gallery', icon: Film, needsScreenShare: true },
                { type: 'screen_only', label: 'Screen Only', icon: Film, needsScreenShare: true },
              ];
              const audioOnlyAsset = recordingAssets.find(a => a.assetType === 'audio_only' && a.processingStatus === 'completed');
              const chatAsset = recordingAssets.find(a => a.assetType === 'chat_txt' && a.processingStatus === 'completed');
              const captionsAsset = recordingAssets.find(a => a.assetType === 'captions_txt' && a.processingStatus === 'completed');
              const participantAudios = recordingAssets.filter(a => a.assetType === 'participant_audio' && a.processingStatus === 'completed');

              return (
                <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden">
                  <div className="px-6 py-4 border-b border-white/10">
                    <h3 className="text-sm font-semibold text-white/90">The recording includes the files listed below</h3>
                  </div>

                  <div className="divide-y divide-white/10">
                    {videoLayouts.filter(layout => {
                      if (layout.needsScreenShare && !recording?.hasScreenShare) return false;
                      const pertrackType = `${layout.type}_pertrack`;
                      return recordingAssets.some(a => a.assetType === layout.type || a.assetType === pertrackType);
                    }).map(layout => {
                      // Resolve to the pertrack mirror when present.
                      const pertrackType = `${layout.type}_pertrack`;
                      const pertrackAsset = recordingAssets.find(a => a.assetType === pertrackType);
                      const legacyAsset = recordingAssets.find(a => a.assetType === layout.type);
                      const asset = (pertrackAsset?.processingStatus === 'completed' ? pertrackAsset : (legacyAsset || pertrackAsset));
                      const resolvedType = asset?.assetType || layout.type;
                      const isReady = asset?.processingStatus === 'completed';
                      const isFailed = asset?.processingStatus === 'failed';
                      const Icon = layout.icon;
                      return (
                        <div key={layout.type} className="px-6 py-3 flex items-center gap-3 hover:bg-white/5 transition-colors">
                          <Icon className="w-4 h-4 text-white/40 flex-shrink-0" />
                          <button
                            onClick={() => isReady && setActiveLayoutType(resolvedType)}
                            disabled={!isReady}
                            className={`text-sm flex-1 text-left ${
                              activeLayoutType === resolvedType ? 'text-[#00c853] font-semibold' : isReady ? 'text-white hover:text-[#00c853]' : 'text-white/30'
                            }`}
                          >
                            {layout.label}
                            {activeLayoutType === resolvedType && <span className="ml-2 text-xs">(Playing)</span>}
                          </button>
                          <span className="text-xs text-white/40 tabular-nums">{formatBytes(asset?.fileSize)}</span>
                          {isReady && (
                            <a
                              href={getAssetDownloadUrl(activeRecordingId, asset.id)}
                              className="p-1.5 hover:bg-white/10 rounded transition-colors text-white/60 hover:text-white"
                              title="Download"
                            >
                              <Download className="w-3.5 h-3.5" />
                            </a>
                          )}
                          {isFailed && <span className="text-xs text-red-400">Failed</span>}
                          {!isReady && !isFailed && <Loader2 className="w-3.5 h-3.5 animate-spin text-white/40" />}
                        </div>
                      );
                    })}

                    {audioOnlyAsset && (
                      <div className="px-6 py-3 flex items-center gap-3 hover:bg-white/5 transition-colors">
                        <span className="text-white/40 flex-shrink-0">🎵</span>
                        <span className="text-sm text-white flex-1">Audio only</span>
                        <span className="text-xs text-white/40 tabular-nums">{formatBytes(audioOnlyAsset.fileSize)}</span>
                        <a href={getAssetDownloadUrl(activeRecordingId, audioOnlyAsset.id)} className="p-1.5 hover:bg-white/10 rounded text-white/60 hover:text-white" title="Download">
                          <Download className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    )}

                    {chatAsset && (
                      <div className="px-6 py-3 flex items-center gap-3 hover:bg-white/5 transition-colors">
                        <MessageSquare className="w-4 h-4 text-white/40 flex-shrink-0" />
                        <span className="text-sm text-white flex-1">Chat file</span>
                        <span className="text-xs text-white/40 tabular-nums">{formatBytes(chatAsset.fileSize)}</span>
                        <a href={getAssetDownloadUrl(activeRecordingId, chatAsset.id)} className="p-1.5 hover:bg-white/10 rounded text-white/60 hover:text-white" title="Download">
                          <Download className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    )}

                    {captionsAsset && (
                      <div className="px-6 py-3 flex items-center gap-3 hover:bg-white/5 transition-colors">
                        <ClipboardList className="w-4 h-4 text-white/40 flex-shrink-0" />
                        <span className="text-sm text-white flex-1">Closed Caption</span>
                        <span className="text-xs text-white/40 tabular-nums">{formatBytes(captionsAsset.fileSize)}</span>
                        <a href={getAssetDownloadUrl(activeRecordingId, captionsAsset.id)} className="p-1.5 hover:bg-white/10 rounded text-white/60 hover:text-white" title="Download">
                          <Download className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    )}

                    {participantAudios.length > 0 && (
                      <div className="px-6 py-3">
                        <div className="text-sm text-white/90 font-medium mb-2 flex items-center gap-2">
                          <ChevronDown className="w-4 h-4" />
                          Audio file of each participant
                        </div>
                        <div className="pl-6 space-y-2">
                          {participantAudios.map(asset => (
                            <div key={asset.id} className="flex items-center gap-3">
                              <span className="text-white/40 flex-shrink-0">🎵</span>
                              <span className="text-sm text-white/80 flex-1">
                                Audio only - {asset.participantName || `Participant ${asset.participantUid}`}
                              </span>
                              <span className="text-xs text-white/40 tabular-nums">{formatBytes(asset.fileSize)}</span>
                              <a href={getAssetDownloadUrl(activeRecordingId, asset.id)} className="p-1.5 hover:bg-white/10 rounded text-white/60 hover:text-white" title="Download">
                                <Download className="w-3.5 h-3.5" />
                              </a>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {layoutProcessingStatus === 'processing' && (
                    <div className="px-6 py-3 bg-white/5 flex items-center gap-2 text-xs text-white/50 border-t border-white/10">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Processing additional recording layouts...
                    </div>
                  )}
                </div>
              );
            })()}

            {/* AI Meeting Notes */}
            {(recording.summary || notes || notesGenerating) && (
              <div className="space-y-4">
                {/* Generating indicator */}
                {notesGenerating && !notes && (
                  <div className="bg-white/5 backdrop-blur-xl border border-[#6a1fbf]/30 rounded-2xl p-6">
                    <div className="flex items-center gap-3">
                      <Loader2 className="w-5 h-5 text-[#6a1fbf] animate-spin" />
                      <div>
                        <h2 className="text-lg font-semibold flex items-center gap-2">
                          <Sparkles className="w-5 h-5 text-[#6a1fbf]" />
                          Generating AI Notes...
                        </h2>
                        <p className="text-sm text-white/50 mt-1">Analyzing transcript with OpenAI to generate structured meeting notes</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Summary Section */}
                {(recording.summary || notes) && (
                <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
                  <button
                    onClick={() => setExpandedSection(expandedSection === 'summary' ? null : 'summary')}
                    className="w-full flex items-center justify-between"
                  >
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                      <Target className="w-5 h-5 text-[#00c853]" />
                      Summary
                    </h2>
                    {expandedSection === 'summary' ? <ChevronDown className="w-4 h-4 text-white/50" /> : <ChevronRight className="w-4 h-4 text-white/50" />}
                  </button>
                  {expandedSection === 'summary' && (
                    <div className="mt-4 space-y-4">
                      {/* Meeting Purpose */}
                      {(notes?.summary?.purpose || recording.summary) && (
                        <div>
                          <h4 className="text-xs font-medium text-white/50 uppercase tracking-wider mb-2">Meeting Purpose</h4>
                          <p className="text-sm text-white/90">{notes?.summary?.purpose || recording.summary}</p>
                        </div>
                      )}

                      {/* Key Takeaways */}
                      {(notes?.summary?.keyTakeaways?.length > 0 || recording.keyPoints?.length > 0) && (
                        <div>
                          <h4 className="text-xs font-medium text-white/50 uppercase tracking-wider mb-2">Key Takeaways</h4>
                          <ul className="space-y-1.5">
                            {(notes?.summary?.keyTakeaways || recording.keyPoints).map((point, idx) => (
                              <li key={idx} className="text-sm text-white/80 flex items-start gap-2">
                                <span className="text-[#00c853] mt-0.5 flex-shrink-0">•</span>
                                <span>{point}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {/* Topics */}
                      {(notes?.summary?.topics?.length > 0 || recording.topics?.length > 0) && (
                        <div>
                          <h4 className="text-xs font-medium text-white/50 uppercase tracking-wider mb-2">Topics Discussed</h4>
                          <div className="flex flex-wrap gap-2">
                            {(notes?.summary?.topics || recording.topics).map((topic, idx) => (
                              <span key={idx} className="px-3 py-1 bg-[#6a1fbf]/20 border border-[#6a1fbf]/30 rounded-full text-xs text-white/80">
                                {topic}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Next Steps */}
                      {notes?.summary?.nextSteps?.length > 0 && (
                        <div>
                          <h4 className="text-xs font-medium text-white/50 uppercase tracking-wider mb-2">Next Steps</h4>
                          <ul className="space-y-1.5">
                            {notes.summary.nextSteps.map((step, idx) => (
                              <li key={idx} className="text-sm text-white/80 flex items-start gap-2">
                                <span className="text-[#6a1fbf] mt-0.5 flex-shrink-0">→</span>
                                <span>{step}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                )}

                {/* Your Questions */}
                {notes?.yourQuestions?.length > 0 && (
                  <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
                    <button
                      onClick={() => setExpandedSection(expandedSection === 'yourQuestions' ? null : 'yourQuestions')}
                      className="w-full flex items-center justify-between"
                    >
                      <h2 className="text-lg font-semibold flex items-center gap-2">
                        <MessageCircleQuestion className="w-5 h-5 text-blue-400" />
                        Your Questions ({notes.yourQuestions.length})
                      </h2>
                      {expandedSection === 'yourQuestions' ? <ChevronDown className="w-4 h-4 text-white/50" /> : <ChevronRight className="w-4 h-4 text-white/50" />}
                    </button>
                    {expandedSection === 'yourQuestions' && (
                      <div className="mt-4 space-y-3">
                        {notes.yourQuestions.map((q, idx) => (
                          <div key={idx} className="p-3 bg-blue-400/5 border border-blue-400/10 rounded-xl">
                            <p className="text-sm font-medium text-blue-300 mb-1">Q: {q.question}</p>
                            {q.answer && (
                              <p className="text-sm text-white/70 pl-4 border-l-2 border-blue-400/30">A: {q.answer}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Their Questions */}
                {notes?.theirQuestions?.length > 0 && (
                  <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
                    <button
                      onClick={() => setExpandedSection(expandedSection === 'theirQuestions' ? null : 'theirQuestions')}
                      className="w-full flex items-center justify-between"
                    >
                      <h2 className="text-lg font-semibold flex items-center gap-2">
                        <MessageCircleQuestion className="w-5 h-5 text-purple-400" />
                        Their Questions ({notes.theirQuestions.length})
                      </h2>
                      {expandedSection === 'theirQuestions' ? <ChevronDown className="w-4 h-4 text-white/50" /> : <ChevronRight className="w-4 h-4 text-white/50" />}
                    </button>
                    {expandedSection === 'theirQuestions' && (
                      <div className="mt-4 space-y-3">
                        {notes.theirQuestions.map((q, idx) => (
                          <div key={idx} className="p-3 bg-purple-400/5 border border-purple-400/10 rounded-xl">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="text-sm font-medium text-purple-300">Q: {q.question}</p>
                            </div>
                            <p className="text-[10px] text-white/40 mb-1">Asked by {q.askedBy}</p>
                            {q.answer && (
                              <p className="text-sm text-white/70 pl-4 border-l-2 border-purple-400/30">A: {q.answer}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Assignments / Action Items */}
                {(notes?.assignments?.length > 0 || recording.actionItems?.length > 0) && (
                  <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
                    <button
                      onClick={() => setExpandedSection(expandedSection === 'actions' ? null : 'actions')}
                      className="w-full flex items-center justify-between"
                    >
                      <h2 className="text-lg font-semibold flex items-center gap-2">
                        <ClipboardList className="w-5 h-5 text-yellow-400" />
                        Action Items ({notes?.assignments?.length || recording.actionItems?.length || 0})
                      </h2>
                      {expandedSection === 'actions' ? <ChevronDown className="w-4 h-4 text-white/50" /> : <ChevronRight className="w-4 h-4 text-white/50" />}
                    </button>
                    {expandedSection === 'actions' && (
                      <div className="mt-4 space-y-2">
                        {notes?.assignments?.length > 0 ? (
                          notes.assignments.map((item, idx) => (
                            <div key={idx} className="p-3 bg-yellow-400/5 border border-yellow-400/10 rounded-xl flex items-start gap-3">
                              <input type="checkbox" className="mt-1 accent-yellow-400" />
                              <div>
                                <p className="text-sm text-white/90">{item.task}</p>
                                <p className="text-[10px] text-white/40 mt-0.5">Assigned to: {item.assignee}</p>
                              </div>
                            </div>
                          ))
                        ) : (
                          recording.actionItems?.map((item, idx) => (
                            <div key={idx} className="p-3 bg-yellow-400/5 border border-yellow-400/10 rounded-xl flex items-start gap-3">
                              <input type="checkbox" className="mt-1 accent-yellow-400" />
                              <p className="text-sm text-white/90">{item}</p>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Bookmarks */}
            {bookmarks.length > 0 && (
              <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6">
                <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                  <Bookmark className="w-5 h-5 text-yellow-400" />
                  Bookmarks
                </h3>
                <div className="space-y-2">
                  {bookmarks.map((bookmark) => (
                    <button
                      key={bookmark.id}
                      onClick={() => handleBookmarkClick(bookmark)}
                      className="w-full text-left p-3 bg-white/5 hover:bg-white/10 rounded-lg transition-colors flex items-center justify-between"
                    >
                      <div>
                        <div className="text-sm font-medium text-white">
                          {bookmark.label || 'Bookmark'}
                        </div>
                        <div className="text-xs text-white/50">{bookmark.userName}</div>
                      </div>
                      <div className="text-xs text-white/70">
                        {formatDuration(Math.floor(bookmark.timestampMs / 1000))}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* B5 — Recording Lifecycle Audit Trail. Surfaces every state
                transition the worker + backend emit, so support can see
                *why* a recording came out broken without grepping pm2 logs.
                Failure events (record-failed-*) are highlighted in red.
                Collapsed by default — most users won't need it. */}
            {Array.isArray(recording?.lifecycle) && recording.lifecycle.length > 0 && (() => {
              const events = recording.lifecycle.slice().sort((a, b) => new Date(a.at) - new Date(b.at));
              const firstAt = events[0] ? new Date(events[0].at).getTime() : 0;
              const hasFailure = events.some(e => typeof e.event === 'string' && e.event.startsWith('record-failed'));
              return (
                <div className={`bg-white/5 backdrop-blur-xl border ${hasFailure ? 'border-red-500/40' : 'border-white/10'} rounded-2xl p-6`}>
                  <button
                    onClick={() => setExpandedSection(expandedSection === 'auditTrail' ? null : 'auditTrail')}
                    className="w-full flex items-center justify-between"
                  >
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Clock className={`w-5 h-5 ${hasFailure ? 'text-red-400' : 'text-white/60'}`} />
                      Recording lifecycle
                      <span className="text-xs text-white/40 font-normal">({events.length})</span>
                      {hasFailure && (
                        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 border border-red-500/30">
                          Failure detected
                        </span>
                      )}
                    </h3>
                    {expandedSection === 'auditTrail'
                      ? <ChevronDown className="w-4 h-4 text-white/50" />
                      : <ChevronRight className="w-4 h-4 text-white/50" />}
                  </button>
                  {expandedSection === 'auditTrail' && (
                    <div className="mt-4 space-y-1 max-h-[480px] overflow-y-auto font-mono text-xs">
                      {events.map((ev, idx) => {
                        const ts = new Date(ev.at).getTime();
                        const elapsedSec = Math.round((ts - firstAt) / 1000);
                        const isFailure = typeof ev.event === 'string' && ev.event.startsWith('record-failed');
                        return (
                          <div
                            key={idx}
                            className={`flex items-start gap-3 p-2 rounded ${isFailure ? 'bg-red-500/10 border border-red-500/30' : 'hover:bg-white/5'}`}
                          >
                            <span className="text-white/40 w-14 flex-shrink-0 tabular-nums">+{elapsedSec}s</span>
                            <span className={`flex-shrink-0 ${isFailure ? 'text-red-300 font-semibold' : 'text-white/80'}`}>
                              {ev.event}
                            </span>
                            {ev.details && (
                              <span className="text-white/40 truncate" title={JSON.stringify(ev.details)}>
                                {JSON.stringify(ev.details).slice(0, 120)}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Right Column: Transcript / Chat */}
          <div className="lg:col-span-1">
            <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 sticky top-24 max-h-[calc(100vh-8rem)] flex flex-col overflow-hidden">
              {/* Tab header */}
              <div className="flex items-center gap-1 mb-4 flex-shrink-0 border-b border-white/10 -mx-6 px-6 pb-3">
                <button
                  onClick={() => setRightSidebarTab('transcript')}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    rightSidebarTab === 'transcript'
                      ? 'bg-[#6a1fbf]/20 text-white border border-[#6a1fbf]/30'
                      : 'text-white/50 hover:text-white/70 hover:bg-white/5'
                  }`}
                >
                  <FileText className="w-4 h-4" />
                  Transcript
                  {activeTranscript.length > 0 && (
                    <span className="text-[10px] text-white/40 ml-1">({activeTranscript.length})</span>
                  )}
                </button>
                <button
                  onClick={() => setRightSidebarTab('chat')}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    rightSidebarTab === 'chat'
                      ? 'bg-[#6a1fbf]/20 text-white border border-[#6a1fbf]/30'
                      : 'text-white/50 hover:text-white/70 hover:bg-white/5'
                  }`}
                >
                  <MessageSquare className="w-4 h-4" />
                  Chat
                  {windowChatMessages.length > 0 && (
                    <span className="text-[10px] text-white/40 ml-1">({windowChatMessages.length})</span>
                  )}
                </button>
              </div>

              {/* Tab content */}
              {rightSidebarTab === 'transcript' ? (
                <TranscriptViewer
                  segments={activeTranscript}
                  currentTimeMs={currentTimeMs}
                  bookmarks={bookmarks}
                  recordingCreatedAt={activeRecording?.createdAt}
                  recordingDurationSec={activeRecording?.duration}
                  recordingStartedAt={activeRecording?.recordingStartedAt}
                  onSeek={handleSeek}
                  onBookmarkClick={handleBookmarkClick}
                />
              ) : (
                <ChatViewer messages={windowChatMessages} />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <RecordingShareModal
          recording={recording}
          allParts={sessionRecordings}
          onClose={() => setShowShareModal(false)}
          onUpdate={loadRecordingData}
        />
      )}

      {/* Clip player modal — overlay to play a saved clip in-page instead of
          opening the raw S3 URL in a new tab. */}
      {playingClip && (
        <ClipPlayerModal
          clip={playingClip}
          onClose={() => setPlayingClip(null)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-[#001233] border border-white/10 rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl">
            <h3 className="text-lg font-semibold text-white mb-2">Delete Recording</h3>
            <p className="text-sm text-white/60 mb-6">
              Are you sure you want to delete "<span className="text-white/80">{recording.name}</span>"? This will permanently remove the video, transcript, notes, and chat. This action cannot be undone.
            </p>
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="px-4 py-2 text-sm text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecordingDetail;
